import React from "react";
import { useLang } from "@/lib/LangContext";
import { BUSINESS } from "@/lib/business";
import Logo from "@/components/Logo";
import { Printer, Download, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

function qrUrl(target, size = 600) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=12&ecc=H&color=6B1F27&bgcolor=FFFFFF&data=${encodeURIComponent(target)}`;
}

function Poster({ title, subtitle, url, testid }) {
  const { lang } = useLang();
  return (
    <div
      data-testid={testid}
      className="poster hairline bg-white p-8 md:p-10 flex flex-col items-center text-center break-inside-avoid"
    >
      <div className="flex items-center gap-3">
        <Logo size={40} variant="dark" />
        <div className="text-left">
          <div className="font-display text-xl tracking-tighter notranslate" translate="no">
            {BUSINESS.name}
          </div>
          <div className="tick-label mt-1">{BUSINESS.address.city}, {BUSINESS.address.state}</div>
        </div>
      </div>

      <div className="tick-label mt-8 text-[#D9333F]">{title}</div>
      <h2 className="font-display text-3xl md:text-4xl tracking-tighter leading-[0.95] mt-3 max-w-xs">
        {subtitle}
      </h2>

      <div className="mt-8 p-4 bg-white hairline-strong">
        <img
          src={qrUrl(url, 520)}
          alt="QR"
          className="w-[260px] h-[260px] block"
        />
      </div>

      <div className="mt-4 tick-label break-all">{url.replace(/^https?:\/\//, "")}</div>

      <div className="mt-8 font-mono-tight text-sm">
        {BUSINESS.phone_display}
      </div>
      <div className="tick-label mt-2">
        {lang === "es" ? "Escanea con tu cámara" : "Scan with your camera"}
      </div>
    </div>
  );
}

export default function MarketingQR() {
  const { lang } = useLang();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const bookUrl = `${origin}/book`;
  const trackUrl = `${origin}/track`;
  const homeUrl = origin;

  const handlePrint = () => window.print();
  const handleDownload = (url, name) => {
    const a = document.createElement("a");
    a.href = qrUrl(url, 1200);
    a.download = `${name}.png`;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  };

  return (
    <div className="min-h-screen bg-[#F7F3F4] text-[#1A1214]">
      {/* Toolbar (hidden on print) */}
      <div className="no-print border-b border-[#6B1F27]/15 bg-white sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex flex-wrap items-center justify-between gap-4">
          <Link to="/admin" className="tick-label flex items-center gap-2 hover:text-[#6B1F27]" data-testid="qr-back">
            <ArrowLeft size={14} /> {lang === "es" ? "Volver al panel" : "Back to admin"}
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleDownload(bookUrl, "cyber-repair-book-qr")}
              data-testid="qr-download-book"
              className="btn-ghost text-sm"
            >
              <Download size={16} /> {lang === "es" ? "Descargar Book" : "Download Book"}
            </button>
            <button
              onClick={() => handleDownload(trackUrl, "cyber-repair-track-qr")}
              data-testid="qr-download-track"
              className="btn-ghost text-sm"
            >
              <Download size={16} /> {lang === "es" ? "Descargar Track" : "Download Track"}
            </button>
            <button onClick={handlePrint} data-testid="qr-print" className="btn-primary text-sm">
              <Printer size={16} /> {lang === "es" ? "Imprimir" : "Print"}
            </button>
          </div>
        </div>
      </div>

      {/* Heading */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 no-print">
        <div className="tick-label">{lang === "es" ? "Marketing" : "Marketing"}</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] mt-4">
          {lang === "es" ? "QR listos para imprimir" : "Print-ready QR codes"}
        </h1>
        <p className="text-neutral-600 mt-4 max-w-2xl">
          {lang === "es"
            ? "Descarga o imprime estos pósteres para pegar en tu tarjeta de negocios, escaparate, volantes o cajas. Al escanear, tu cliente va directo a agendar o rastrear su reparación."
            : "Download or print these posters for your business card, storefront, flyers, or boxes. Scanning takes your customer straight to booking or tracking."}
        </p>
      </section>

      {/* Posters grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="qr-posters">
          <Poster
            testid="poster-book"
            title={lang === "es" ? "Agenda tu reparación" : "Book your repair"}
            subtitle={lang === "es" ? "Escanea. Agenda. Listo." : "Scan. Book. Done."}
            url={bookUrl}
          />
          <Poster
            testid="poster-track"
            title={lang === "es" ? "Rastrea tu reparación" : "Track your repair"}
            subtitle={lang === "es" ? "Sigue tu cita en vivo." : "Follow your appointment live."}
            url={trackUrl}
          />
        </div>

        {/* Tips (hidden on print) */}
        <div className="no-print mt-10 hairline bg-white p-8">
          <div className="tick-label">{lang === "es" ? "Consejos de impresión" : "Print tips"}</div>
          <ul className="mt-4 text-sm text-neutral-700 space-y-2 leading-relaxed">
            <li>
              {lang === "es"
                ? "Tamaño mínimo recomendado del QR impreso: 3 × 3 cm (1.2 in). Menos puede no leerse."
                : "Recommended minimum printed QR size: 3 × 3 cm (1.2 in). Smaller may not scan."}
            </li>
            <li>
              {lang === "es"
                ? "Mantén un margen blanco alrededor del QR (ya está incluido en el póster)."
                : "Keep a white margin around the QR (already included in the poster)."}
            </li>
            <li>
              {lang === "es"
                ? "Prueba a escanearlo con tu celular antes de imprimir en grande."
                : "Test-scan it with your phone before printing at large scale."}
            </li>
            <li>
              {lang === "es"
                ? "Si cambias de dominio, regenera los QR — apuntan a la URL actual del navegador."
                : "If you switch domains, regenerate the QRs — they point to the current browser URL."}
            </li>
          </ul>
        </div>

        {/* Extra: home URL small QR */}
        <div className="no-print mt-6 hairline bg-white p-8 flex items-center gap-6">
          <img src={qrUrl(homeUrl, 300)} alt="Home QR" className="w-[120px] h-[120px]" />
          <div>
            <div className="tick-label">{lang === "es" ? "Sitio principal" : "Main site"}</div>
            <div className="font-mono-tight text-sm mt-1 break-all">{homeUrl}</div>
            <button
              onClick={() => handleDownload(homeUrl, "cyber-repair-home-qr")}
              data-testid="qr-download-home"
              className="btn-ghost text-sm mt-4"
            >
              <Download size={16} /> {lang === "es" ? "Descargar" : "Download"}
            </button>
          </div>
        </div>
      </section>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .poster { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; border: 1px solid #ccc; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
    </div>
  );
}
